import { useLazyQuery, useQuery } from '@apollo/client';
import { notEmpty } from '@crehana/utils';

import useLatestThreeMonthUserActivity from '../../pages/Metrics/General/hooks/useLatestThreeMonthUserActivity';
import useAdminDashboard from '../useAdminDashboard';
import useConditionalQuery from '../useConditionalQuery';
import GET_DASHBOARD_METRICS_QUERY from './graphql/GetDashboardMetrics.b2b.graphql';
import GET_LICENSES_DATA from './graphql/GetLicensesData.v5.graphql';
import GET_LICENSES_LEGACY from './graphql/GetLicensesLegacy.b2b.graphql';
import type {
  GetDashboardMetrics,
  GetDashboardMetricsVariables,
} from './graphql/types/GetDashboardMetrics';
import type {
  GetLicensesData,
  GetLicensesDataVariables,
} from './graphql/types/GetLicensesData';
import type {
  GetLicensesLegacy,
  GetLicensesLegacyVariables,
} from './graphql/types/GetLicensesLegacy';

const useDashboardMetrics = ({ slug }: { slug: string }) => {
  const { organizationId } = useAdminDashboard();

  const { threeMonthsData, loading: loadingUsageData } =
    useLatestThreeMonthUserActivity({
      organizationId: organizationId.toString(),
    });

  const [
    getLicensesLegacy,
    { data: dataLicenses, loading: loadingLicenses, error: errorLicenses },
  ] = useLazyQuery<GetLicensesLegacy, GetLicensesLegacyVariables>(
    GET_LICENSES_LEGACY,
    {
      variables: {
        organization: slug,
      },
      context: { clientName: 'b2b' },
    },
  );

  const [
    getLicensesData,
    { data: fakeDataLicenses, loading: fakeLoadingLicenses },
  ] = useLazyQuery<GetLicensesData, GetLicensesDataVariables>(
    GET_LICENSES_DATA,
    {
      variables: {
        organizationId: organizationId.toString(),
      },
      context: { clientName: 'v5' },
    },
  );

  const { isActive: hasDataNewDashboard } = useConditionalQuery({
    featureFlag: 'data-new-dashboard',
    getData: () => getLicensesData(),
    getDataLegacy: () => getLicensesLegacy(),
  });

  const { data, loading, error, updateQuery } = useQuery<
    GetDashboardMetrics,
    GetDashboardMetricsVariables
  >(GET_DASHBOARD_METRICS_QUERY, {
    variables: {
      organization: slug,
    },
    context: { clientName: 'b2b' },
  });

  const onSendRequest = () => {
    updateQuery(prev => {
      if (prev?.organization) {
        return {
          ...prev,
          organization: {
            ...prev?.organization,
          },
        };
      }
      return prev;
    });
  };

  // OrganizationMetrics
  const studyHours =
    data?.organization?.organizationDailyStats?.studyHours || null;
  const averageStudyHours =
    data?.organization?.organizationDailyStats?.averageStudyHours || null;
  const globalProgress =
    data?.organization?.organizationDailyStats?.globalProgress || null;
  const certifiedProjects =
    data?.organization?.organizationDailyStats?.certifiedProjects || null;

  // Upper Metrics
  const licensesQuantity = hasDataNewDashboard
    ? fakeDataLicenses?.dasboard?.available_licenses || 0
    : dataLicenses?.organization?.funnelData?.licensesQuantity || null;
  const registeredUsers = hasDataNewDashboard
    ? fakeDataLicenses?.dasboard?.stock_registered || 0
    : dataLicenses?.organization?.funnelData?.registeredUsers || null;
  const activeUsers = hasDataNewDashboard
    ? fakeDataLicenses?.dasboard?.stock_activated || 0
    : dataLicenses?.organization?.funnelData?.activeUsers || null;
  const studyingUsers = hasDataNewDashboard
    ? fakeDataLicenses?.dasboard?.usage_users || 0
    : dataLicenses?.organization?.funnelData?.studyingUsers || null;

  // Lower Metrics
  const availableLicences = hasDataNewDashboard
    ? (fakeDataLicenses?.dasboard?.available_licenses || 0) -
      (fakeDataLicenses?.dasboard?.stock_registered || 0)
    : dataLicenses?.organization?.funnelData?.availableLicences || 0;
  const registeredUsersPercent = hasDataNewDashboard
    ? 0
    : dataLicenses?.organization?.funnelData?.registeredUsersPercent || 0;
  const pendingUsers = hasDataNewDashboard
    ? (fakeDataLicenses?.dasboard?.stock_registered || 0) -
      (fakeDataLicenses?.dasboard?.stock_activated || 0)
    : dataLicenses?.organization?.funnelData?.pendingUsers || 0;
  const activeUsersPercent = hasDataNewDashboard
    ? fakeDataLicenses?.dasboard?.usage_users || 0
    : dataLicenses?.organization?.funnelData?.activeUsersPercent || 0;
  const notStudyingUsers = hasDataNewDashboard
    ? (fakeDataLicenses?.dasboard?.stock_activated || 0) -
      (fakeDataLicenses?.dasboard?.usage_users || 0)
    : dataLicenses?.organization?.funnelData?.notStudyingUsers || 0;
  const studyingUsersPercent = hasDataNewDashboard
    ? 0
    : dataLicenses?.organization?.funnelData?.studyingUsersPercent || 0;
  const orderRequestAlreadySent = hasDataNewDashboard
    ? false
    : dataLicenses?.organization?.funnelData?.orderRequestAlreadySent || false;

  // Summary Users
  const activityOrganization = data?.organization?.organizationStats
    ?.activityOrganization
    ? {
        activityCurrent:
          data?.organization?.organizationStats?.activityOrganization.activityCurrent
            ?.filter(notEmpty)
            // TODO: talk with the code owner to fix this code so we can enable this rule again
            .map(({ __typename, ...rest }) => rest) || [],
        activityPrevious:
          data.organization.organizationStats.activityOrganization.activityPrevious
            ?.filter(notEmpty)
            // TODO: talk with the code owner to fix this code so we can enable this rule again

            .map(({ __typename, ...rest }) => rest) || [],
      }
    : {
        activityCurrent: [{ date: '', seconds: 0 }],
        activityPrevious: [{ date: '', seconds: 0 }],
      };

  return {
    studyHours,
    averageStudyHours,
    globalProgress,
    certifiedProjects,
    licensesQuantity,
    availableLicences,
    registeredUsersPercent,
    activeUsers,
    registeredUsers,
    pendingUsers,
    activeUsersPercent,
    activityOrganization,
    loading: loading || loadingUsageData,
    loadingLicenses: loadingLicenses || fakeLoadingLicenses,
    errorLicenses,
    error,
    studyingUsersPercent,
    notStudyingUsers,
    studyingUsers,
    usageData: threeMonthsData,
    orderRequestAlreadySent,
    onSendRequest,
  };
};

export default useDashboardMetrics;
